import { useCallback, useEffect, useRef, useState } from "react";

import { AxiosResponse } from "axios";

import { axiosXero } from "../../api";

import type { IXeroState } from "../types";

/**
 * Per-user Xero connection state. `connected` is `undefined` while loading, and
 * stays `undefined` when `enabled` is false — callers that only need the state
 * behind a module gate pass the gate in so uninstalled customers make no request.
 * `refresh` re-reads it after a connect/disconnect.
 *
 * `expired` is a credential row whose refresh token is dead. The service reports
 * it as `connected: false, expired: true`, so `expired` is the only thing that
 * says the row exists and can be reconnected or disconnected.
 *
 * `unavailable` is the request itself failing — a 401, a 5xx, no network. That
 * is not an answer about the connection, so `connected` stays `undefined`: a
 * screen that turned it into `false` would tell a connected user to connect.
 */
export function useXeroConnected(enabled = true) {
  const [state, setState] = useState<IXeroState>();
  const [unavailable, setUnavailable] = useState(false);
  // A disconnect, a retry and the load can all be in flight at once. Only the
  // newest answer may land, and none may land after the caller has gone.
  const mounted = useRef(true);
  const generation = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(() => {
    if (!enabled) {
      return;
    }
    generation.current += 1;
    const asked = generation.current;
    const current = () => mounted.current && generation.current === asked;

    axiosXero
      .get("/state")
      .then((resp: AxiosResponse<IXeroState>) => {
        if (!current()) {
          return;
        }
        setState(resp.data);
        setUnavailable(false);
      })
      .catch((e) => {
        if (!current()) {
          return;
        }
        console.debug("Xero state unavailable: ", e);
        setState(undefined);
        setUnavailable(true);
      });
  }, [enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    connected: state?.connected,
    /** GET /state failed, so nothing is known about the connection. */
    unavailable,
    expired: state?.expired ?? false,
    wrongOrg: state?.wrong_org ?? false,
    /**
     * A connection something can actually be pushed or read through. An expired
     * grant and a row pinned to another Xero organisation both 403 every call,
     * so neither counts, and neither does a state that has not answered yet.
     */
    usable:
      state?.connected === true && !state.expired && !state.wrong_org,
    organisation: state?.organisation ?? null,
    email: state?.email ?? null,
    connectedAt: state?.connected_at ?? null,
    lastUsedAt: state?.last_used_at ?? null,
    refresh,
  };
}

/**
 * One page's connection state, as `useXeroConnected` hands it over. Pass it to
 * `useXeroPush` so a page that already reads the state does not read it twice.
 */
export type XeroConnection = ReturnType<typeof useXeroConnected>;
